import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { DEFAULT_MACRO_GOALS, DEFAULT_FITNESS_GOALS, STORAGE_KEY } from "../constants/config";
import { buildBodyCompositionAnchor, calcMacros, projectMonthlyUsage } from "../utils/calculations";
import { getTodayDate, getISOWeek } from "../utils/formatting";
import { apiFetch, clearApiToken, saveApiToken } from "../utils/apiClient";

const LOG_KEY       = "fitlog_debug_log";
const BACKUP_URL    = process.env.NODE_ENV === "development"
  ? "http://localhost:8788/api/backup"
  : "/api/backup";
const STATE_URL     = process.env.NODE_ENV === "development"
  ? "http://localhost:8788/api/state"
  : "/api/state";
const LS_BACKUP_KEY = "fitlog_last_backup";
const FAILED_WRITE_KEY = "fitlog_failed_writes";
const ALERTS_URL    = process.env.NODE_ENV === "development"
  ? "http://localhost:8788/api/alerts"
  : "/api/alerts";
const WEIGHT_IMAGE_URL = process.env.NODE_ENV === "development"
  ? "http://localhost:8788/api/weight-image"
  : "/api/weight-image";
const INGEST_URL = process.env.NODE_ENV === "development"
  ? "http://localhost:8788/api/ingest"
  : "/api/ingest";

function triggerDailyBackup() {
  const today = new Date().toISOString().slice(0, 10);
  if (localStorage.getItem(LS_BACKUP_KEY) === today) return;
  apiFetch(BACKUP_URL, { method: "POST" })
    .then(r => r.json())
    .then(d => { if (d.ok) localStorage.setItem(LS_BACKUP_KEY, today); })
    .catch(() => {});
}

function isSyncedWorkoutItem(workout) {
  return String(workout?.sourceId || "").startsWith("health_connect_")
    || String(workout?.id || "").startsWith("hc-");
}

function shouldUpdateBodyCompositionAnchor(next = {}, previous = {}) {
  if (!next.bodyFatAnchorWeight || !next.bodyFatAnchorPercent) return true;
  return ["neck", "waist", "height", "bodyFatCurrent", "weightCurrent"]
    .some(key => Number(next?.[key]) !== Number(previous?.[key]));
}

export function useAppState() {
  const [dataLoading,   setDataLoading]   = useState(true);
  const [dataLoadError, setDataLoadError] = useState("");
  const [saveStatus, setSaveStatus] = useState({ state: "idle", category: null, error: "" });

  // ── Navigation ────────────────────────────────────────────────────────────
  const [view, setView] = useState("Dashboard");

  // ── Meal data ─────────────────────────────────────────────────────────────
  const [mealPlans,       setMealPlans]       = useState({}); // { id: { id, name, days } }
  const [weekAssignments, setWeekAssignments] = useState({}); // { "2026-W10": planId }
  const [logs,            setLogs]            = useState([]);
  const [activeMeal,      setActiveMeal]      = useState({ meal: null, ingredients: [], notes: "", isEdit: false });
  const [logDate,         setLogDate]         = useState(getTodayDate());

  // ── Grocery data ──────────────────────────────────────────────────────────
  const [groceries,     setGroceries]    = useState([]);
  const [projections,   setProj]         = useState([]);
  const [suggestions,   setSuggestions]  = useState([]);
  const [loadingSug,    setLoadingSug]   = useState(false);
  const [groceryInput,  setGroceryInput] = useState("");
  const [groceryAmt,    setGroceryAmt]   = useState("");
  const [groceryUnit,   setGroceryUnit]  = useState("g");

  // ── Goals ─────────────────────────────────────────────────────────────────
  const [macroGoals,       setMacroGoals]       = useState(DEFAULT_MACRO_GOALS);
  const [fitnessGoals,     setFitnessGoals]     = useState(DEFAULT_FITNESS_GOALS);
  const [editFitnessGoals, setEditFitnessGoals] = useState(false);
  const [editMacroGoals,   setEditMacroGoals]   = useState(false);
  const [visibleGoalCards, setVisibleGoalCards] = useState({ bodyFat: true, weight: true, muscle: true, run: false });

  // ── Custom food library ───────────────────────────────────────────────────
  const [customFoods,    setCustomFoods]   = useState([]);

  // ── Workouts & weight ─────────────────────────────────────────────────────
  const [workouts,       setWorkouts]      = useState([]);
  const [weightLog,      setWeightLog]     = useState([]);
  const [todayWeighIn,   setTodayWeighIn]  = useState("");
  const [progressTab,    setProgressTab]   = useState("Weight");
  const [parsingWorkout, setParsing2]      = useState(false);

  // ── Chat history ──────────────────────────────────────────────────────────
  const [chatMessages,    setChatMessages]    = useState([]); // display messages
  const [chatApiMessages, setChatApiMessages] = useState([]); // API-formatted messages

  // ── API usage tracking ────────────────────────────────────────────────────
  const [apiUsage, setApiUsage] = useState({ inputTokens: 0, outputTokens: 0, budget: 5.00 });

  // ── UI state ──────────────────────────────────────────────────────────────
  const [toast,          setToast]          = useState("");
  const [parsing,        setParsing]        = useState({ meal: false, grocery: false });
  const [uploadProgress, setUploadProgress] = useState({ active: false, step: "", pct: 0 });
  const [parseErr,       setParseErr]       = useState("");
  const [debugLog,       setDebugLog]       = useState([]);
  const [showDebug,      setShowDebug]      = useState(false);
  const [chartMetric,    setChartMetric]    = useState("calories");
  const [timeRange,      setTimeRange]      = useState("month");
  const [systemAlerts,   setSystemAlerts]   = useState([]);
  const [authRequired,   setAuthRequired]   = useState(false);
  const [healthConnect,  setHealthConnect]  = useState({ daily: [], workouts: 0, steps: 0, heartRate: 0, dailyEnergy: 0, lastSyncedAt: null });

  // ── Refs ──────────────────────────────────────────────────────────────────
  const mealFileRef    = useRef();
  const groceryFileRef = useRef();
  const watchFileRef   = useRef();
  const dataLoadedRef  = useRef(false);
  const revisionsRef   = useRef({});

  useEffect(() => {
    const onAuthRequired = () => setAuthRequired(true);
    window.addEventListener("fitlog:auth-required", onAuthRequired);
    return () => window.removeEventListener("fitlog:auth-required", onAuthRequired);
  }, []);

  function unlockApi(token) {
    saveApiToken(token);
    setAuthRequired(false);
    window.location.reload();
  }

  function logoutApi() {
    clearApiToken();
    setAuthRequired(true);
  }

  // ── Live macro preview ────────────────────────────────────────────────────
  const _ingKey = JSON.stringify(activeMeal.ingredients);
  const liveMacros = useMemo(() => calcMacros(activeMeal.ingredients), [_ingKey]); // value-equality dep via JSON.stringify

  // ── Active plan helper ────────────────────────────────────────────────────
  function getActivePlan(dateStr) {
    const weekKey = getISOWeek(dateStr);
    const planId  = weekAssignments[weekKey];
    if (planId && mealPlans[planId]) return mealPlans[planId];
    // Fallback: most recently added plan
    const plans = Object.values(mealPlans);
    return plans.length > 0 ? plans[plans.length - 1] : null;
  }

  // ── Load from R2 on mount ─────────────────────────────────────────────────
  useEffect(() => {
    apiFetch(STATE_URL)
      .then(r => {
        if (r.status === 401) {
          setAuthRequired(true);
          throw new Error("auth-required");
        }
        if (!r.ok) throw new Error(`State load failed (${r.status})`);
        return r.json();
      })
      .then(d => {
        revisionsRef.current = d._meta?.revisions || {};
        if (d.logs)            setLogs(d.logs);
        if (d.groceries)       setGroceries(d.groceries);
        if (d.macroGoals)      setMacroGoals(d.macroGoals);
        if (d.fitnessGoals)    setFitnessGoals(d.fitnessGoals);
        if (d.customFoods)     { setCustomFoods(d.customFoods); syncCustomFoodsCache(d.customFoods); }
        if (d.workouts)        setWorkouts(d.workouts);
        if (d.weightLog)       setWeightLog(d.weightLog);
        if (d.healthConnect)   setHealthConnect(d.healthConnect);
        if (d.weekAssignments) setWeekAssignments(d.weekAssignments);
        if (d.mealPlans) {
          setMealPlans(d.mealPlans);
        } else if (d.mealPlan) {
          // Migrate old single mealPlan → mealPlans
          const id = "plan_legacy";
          setMealPlans({ [id]: { id, name: "Plan 1", days: d.mealPlan.days } });
        }
        if (d.debugLog)        setDebugLog(d.debugLog);
        if (d.chatMessages)    setChatMessages(d.chatMessages);
        if (d.chatApiMessages) setChatApiMessages(d.chatApiMessages);
        if (d.apiUsage)        setApiUsage(a => ({ ...a, ...d.apiUsage }));

        dataLoadedRef.current = true;

        // ── One-time migration: if R2 is empty, recover data from localStorage ──
        const r2Empty = !d.logs?.length && !d.groceries?.length &&
                        !d.weightLog?.length && !Object.keys(d.mealPlans || {}).length;
        if (r2Empty) {
          try {
            const raw = localStorage.getItem("fitlog_v3");
            if (!raw) return;
            const local = JSON.parse(raw);
            const hasData = local.logs?.length || local.groceries?.length ||
                            local.weightLog?.length || local.workouts?.length;
            if (!hasData) return;

            // Apply localStorage data to state
            if (local.logs)            setLogs(local.logs);
            if (local.groceries)       setGroceries(local.groceries);
            if (local.macroGoals)      setMacroGoals(local.macroGoals);
            if (local.fitnessGoals)    setFitnessGoals(local.fitnessGoals);
            if (local.customFoods)     { setCustomFoods(local.customFoods); syncCustomFoodsCache(local.customFoods); }
            if (local.weightLog)       setWeightLog(local.weightLog);
            if (local.weekAssignments) setWeekAssignments(local.weekAssignments);
            if (local.chatMessages)    setChatMessages(local.chatMessages);
            if (local.chatApiMessages) setChatApiMessages(local.chatApiMessages);
            if (local.apiUsage)        setApiUsage(a => ({ ...a, ...local.apiUsage }));

            // Merge workouts: keep R2 entries not in localStorage, add all localStorage entries
            const localWorkouts = local.workouts || [];
            const r2Workouts    = d.workouts || [];
            const r2OnlyWorkouts = r2Workouts.filter(
              rw => !localWorkouts.some(lw => lw.date === rw.date && lw.workoutType === rw.workoutType)
            );
            const mergedWorkouts = [...localWorkouts, ...r2OnlyWorkouts];
            setWorkouts(mergedWorkouts);

            // Resolve mealPlans
            let mps = {};
            if (local.mealPlans)      mps = local.mealPlans;
            else if (local.mealPlan)  mps = { plan_legacy: { id: "plan_legacy", name: "Plan 1", days: local.mealPlan.days } };
            if (Object.keys(mps).length) setMealPlans(mps);

            forceMigrate({ ...local, workouts: mergedWorkouts, mealPlans: mps }).catch(() => {});

            setToast("Data recovered from local backup");
            setTimeout(() => setToast(""), 4000);
          } catch {}
        }
      })
      .catch(err => {
        dataLoadedRef.current = false;
        setDataLoadError(err.message || "Unable to load your data");
      })
      .finally(() => setDataLoading(false));
  }, []);

  // ── Load system alerts ────────────────────────────────────────────────────
  const refreshRemoteState = useCallback(async () => {
    const response = await apiFetch(STATE_URL);
    if (!response.ok) throw new Error(`State refresh failed (${response.status})`);
    const data = await response.json();
    revisionsRef.current = data._meta?.revisions || revisionsRef.current;
    if (data.logs) setLogs(data.logs);
    if (data.groceries) setGroceries(data.groceries);
    if (data.workouts) setWorkouts(data.workouts);
    if (data.weightLog) setWeightLog(data.weightLog);
    if (data.healthConnect) setHealthConnect(data.healthConnect);
    return data;
  }, []);

  useEffect(() => {
    apiFetch(ALERTS_URL)
      .then(r => r.json())
      .then(d => { if (Array.isArray(d.alerts)) setSystemAlerts(d.alerts); })
      .catch(() => {});
  }, []);

  // ── Update projections when plans or groceries change ────────────────────
  useEffect(() => {
    try {
      setProj(projectMonthlyUsage(groceries, mealPlans, weekAssignments));
    } catch (err) {
      console.error("[projectMonthlyUsage]", err);
    }
  }, [mealPlans, weekAssignments, groceries]);

  // ── Custom foods cache sync (localStorage for instant ingredient search) ──
  function syncCustomFoodsCache(foods) {
    try {
      const cache = JSON.parse(localStorage.getItem("fitlog_food_cache") || "{}");
      foods.forEach(f => { cache[f.name.toLowerCase().trim()] = { calories: f.calories, protein: f.protein, carbs: f.carbs, fat: f.fat, fiber: f.fiber }; });
      localStorage.setItem("fitlog_food_cache", JSON.stringify(cache));
      localStorage.setItem("fitlog_custom_foods", JSON.stringify(foods));
    } catch {}
  }

  // ── Force migration: push any data object directly to state + R2 ─────────
  function rememberFailedWrite(write) {
    try {
      const current = JSON.parse(localStorage.getItem(FAILED_WRITE_KEY) || "{}");
      current[write.category] = { ...write, failedAt: new Date().toISOString() };
      localStorage.setItem(FAILED_WRITE_KEY, JSON.stringify(current));
    } catch {}
  }

  function clearFailedWrite(category) {
    try {
      const current = JSON.parse(localStorage.getItem(FAILED_WRITE_KEY) || "{}");
      delete current[category];
      localStorage.setItem(FAILED_WRITE_KEY, JSON.stringify(current));
    } catch {}
  }

  function mergeLogConflict(baseData, intendedData, currentData) {
    const base = new Map((baseData || []).map(day => [day.date, day]));
    const intended = new Map((intendedData || []).map(day => [day.date, day]));
    const merged = new Map((currentData || []).map(day => [day.date, day]));
    const dates = new Set([...base.keys(), ...intended.keys()]);

    for (const date of dates) {
      const before = base.get(date);
      const after = intended.get(date);
      if (JSON.stringify(before) === JSON.stringify(after)) continue;
      if (after === undefined) {
        merged.delete(date);
        continue;
      }
      if (before === undefined) {
        merged.set(date, after);
        continue;
      }

      const current = merged.get(date) || { id: after.id || date, date, meals: {} };
      const currentMeals = { ...(current.meals || {}) };
      const beforeMeals = before.meals || {};
      const afterMeals = after.meals || {};
      const mealNames = new Set([...Object.keys(beforeMeals), ...Object.keys(afterMeals)]);

      for (const mealName of mealNames) {
        if (JSON.stringify(beforeMeals[mealName]) === JSON.stringify(afterMeals[mealName])) continue;
        if (afterMeals[mealName] === undefined) delete currentMeals[mealName];
        else currentMeals[mealName] = afterMeals[mealName];
      }

      merged.set(date, { ...current, ...after, meals: currentMeals });
    }

    return [...merged.values()].sort((a, b) => a.date.localeCompare(b.date));
  }

  async function persistCategory(category, data, {
    allowLargeDecrease = false,
    source = "web_app",
    baseData,
    onRebased,
  } = {}) {
    if (!dataLoadedRef.current) {
      setSaveStatus({ state: "failed", category, error: "Cloud data has not loaded. Nothing was saved." });
      return false;
    }
    const expectedRevision = revisionsRef.current[category];
    if (!expectedRevision) {
      setSaveStatus({ state: "failed", category, error: `Missing revision for ${category}. Reload before saving.` });
      return false;
    }
    const write = { category, data, expectedRevision, allowLargeDecrease, source, baseData };
    setSaveStatus({ state: "saving", category, error: "" });
    try {
      let pending = write;
      for (let attempt = 0; attempt < 2; attempt++) {
        const res = await apiFetch(STATE_URL, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(pending),
        });
        const result = await res.json().catch(() => ({}));

        if (res.status === 409 && category === "logs" && result.currentRevision && Array.isArray(result.currentData)) {
          const rebased = mergeLogConflict(baseData, pending.data, result.currentData);
          pending = { ...pending, data: rebased, expectedRevision: result.currentRevision, source: "web_app_conflict_rebase" };
          revisionsRef.current[category] = result.currentRevision;
          continue;
        }

        if (!res.ok || !result.ok) throw new Error(result.error || `Save failed (${res.status})`);
        revisionsRef.current[category] = result.revision;
        clearFailedWrite(category);
        if (pending.data !== data) onRebased?.(pending.data);
        setSaveStatus({ state: "saved", category, error: "" });
        triggerDailyBackup();
        setTimeout(() => setSaveStatus(s => s.category === category ? { state: "idle", category: null, error: "" } : s), 2500);
        return true;
      }
      throw new Error("Meal log changed again while saving. Retry once more.");
    } catch (error) {
      rememberFailedWrite(write);
      setSaveStatus({ state: "failed", category, error: error.message || "Save failed" });
      return false;
    }
  }

  async function retryFailedWrites() {
    let failed = {};
    try { failed = JSON.parse(localStorage.getItem(FAILED_WRITE_KEY) || "{}"); } catch {}
    for (const write of Object.values(failed)) {
      const ok = await persistCategory(write.category, write.data, {
        allowLargeDecrease: write.allowLargeDecrease,
        source: "retry_queue",
        baseData: write.baseData,
      });
      if (!ok) return false;
    }
    setSaveStatus({ state: "idle", category: null, error: "" });
    return true;
  }

  function saveCategory(category, next, previous, setter, options) {
    setter(next);
    return persistCategory(category, next, { ...options, baseData: previous, onRebased: setter }).then(ok => {
      if (!ok) setter(previous);
      return ok;
    });
  }

  async function forceMigrate(d) {
    const mps = d.mealPlans
      ? d.mealPlans
      : d.mealPlan
        ? { plan_legacy: { id: "plan_legacy", name: "Plan 1", days: d.mealPlan.days } }
        : {};
    const mergedWorkouts = [
      ...(d.workouts || []),
      ...(workouts || []).filter(
        rw => !(d.workouts || []).some(lw => lw.date === rw.date && lw.workoutType === rw.workoutType)
      ),
    ];

    if (d.logs)            setLogs(d.logs);
    if (d.groceries)       setGroceries(d.groceries);
    if (d.macroGoals)      setMacroGoals(d.macroGoals);
    if (d.fitnessGoals)    setFitnessGoals(d.fitnessGoals);
    if (d.customFoods)     { setCustomFoods(d.customFoods); syncCustomFoodsCache(d.customFoods); }
    if (d.weightLog)       setWeightLog(d.weightLog);
    if (d.weekAssignments) setWeekAssignments(d.weekAssignments);
    if (d.chatMessages)    setChatMessages(d.chatMessages);
    if (d.chatApiMessages) setChatApiMessages(d.chatApiMessages);
    if (d.apiUsage)        setApiUsage(a => ({ ...a, ...d.apiUsage }));
    setWorkouts(mergedWorkouts);
    if (Object.keys(mps).length) setMealPlans(mps);

    const incoming = {
      logs: d.logs || [], groceries: d.groceries || [],
      macroGoals: d.macroGoals || DEFAULT_MACRO_GOALS,
      fitnessGoals: d.fitnessGoals || DEFAULT_FITNESS_GOALS,
      workouts: mergedWorkouts, weightLog: d.weightLog || [],
      mealPlans: mps, weekAssignments: d.weekAssignments || {},
      chatMessages: d.chatMessages || [], chatApiMessages: d.chatApiMessages || [],
      apiUsage: d.apiUsage || { inputTokens: 0, outputTokens: 0, budget: 5.00 },
      customFoods: d.customFoods || [],
    };
    for (const [category, data] of Object.entries(incoming)) {
      const ok = await persistCategory(category, data, { allowLargeDecrease: true, source: "manual_recovery" });
      if (!ok) return false;
    }
    return true;
  }

  // ── Persistence ───────────────────────────────────────────────────────────
  function saveLogs(next)            { return saveCategory("logs", next, logs, setLogs); }
  function saveGroceries(next)       { setSuggestions([]); return saveCategory("groceries", next, groceries, setGroceries); }
  function saveMacroGoals(next)      { return saveCategory("macroGoals", next, macroGoals, setMacroGoals); }
  function saveFitnessGoals(next) {
    const enriched = shouldUpdateBodyCompositionAnchor(next, fitnessGoals)
      ? buildBodyCompositionAnchor(next, weightLog)
      : next;
    return saveCategory("fitnessGoals", enriched, fitnessGoals, setFitnessGoals);
  }
  function saveWorkouts(next) {
    const syncedWorkouts = (workouts || []).filter(isSyncedWorkoutItem);
    const nextManualWorkouts = (next || []).filter(item => !isSyncedWorkoutItem(item));
    const optimisticWorkouts = [...nextManualWorkouts, ...syncedWorkouts];
    setWorkouts(optimisticWorkouts);
    return persistCategory("workouts", nextManualWorkouts, {
      allowLargeDecrease: true,
      source: "manual_workout_update",
      baseData: (workouts || []).filter(item => !isSyncedWorkoutItem(item)),
      onRebased: rebased => setWorkouts([...(rebased || []), ...syncedWorkouts]),
    }).then(async ok => {
      if (!ok) {
        setWorkouts(workouts);
        return false;
      }
      await refreshRemoteState().catch(() => {});
      return true;
    });
  }
  async function deleteFitnessWorkout(workout) {
    try {
      const response = await apiFetch(INGEST_URL, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: workout.id,
          sourceId: workout.sourceId,
          source: workout.source,
          reason: "user_deleted",
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.ok) throw new Error(result.error || `Delete failed (${response.status})`);
      await refreshRemoteState();
      return true;
    } catch (error) {
      setSaveStatus({ state: "failed", category: "workouts", error: error.message || "Delete failed" });
      return false;
    }
  }
  function saveCustomFoods(next)     { syncCustomFoodsCache(next); return saveCategory("customFoods", next, customFoods, setCustomFoods); }
  function saveMealPlans(next)       { return saveCategory("mealPlans", next, mealPlans, setMealPlans); }
  function saveWeekAssignments(next) { return saveCategory("weekAssignments", next, weekAssignments, setWeekAssignments); }

  function saveApiUsage(au) {
    return saveCategory("apiUsage", au, apiUsage, setApiUsage);
  }

  function saveChatHistory(cm, cam) {
    // Strip image previews and base64 from display messages (too large to store)
    const stripped = cm.map(m => ({
      ...m,
      files: m.files?.map(f => ({ name: f.name, type: f.type })),
    }));
    // Strip image base64 from API messages, replace with text placeholder
    const strippedApi = cam.map(m => {
      if (!Array.isArray(m.content)) return m;
      return {
        ...m,
        content: m.content.map(b =>
          b.type === "image" ? { type: "text", text: "[Image uploaded by user]" } : b
        ),
      };
    });
    saveCategory("chatMessages", stripped, chatMessages, setChatMessages);
    saveCategory("chatApiMessages", strippedApi, chatApiMessages, setChatApiMessages);
  }

  /** Atomic weight log update — saves both weightLog and fitnessGoals.weightCurrent together */
  async function saveWeightEntry(kg, photo = null, photoMetadata = null) {
    const today = getTodayDate();
    let uploaded = {};
    let previousPhoto = null;
    if (photo) {
      setSaveStatus({ state: "saving", category: "weight photo", error: "" });
      try {
        const response = await apiFetch(WEIGHT_IMAGE_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ date: today, image: photo, metadata: photoMetadata }),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result.ok) throw new Error(result.error || `Photo upload failed (${response.status})`);
        uploaded = {
          photoPath: result.photoPath,
          photoUrl: result.photoUrl,
          photoMetadata: result.metadata || photoMetadata || null,
        };
        previousPhoto = result.previous || null;
      } catch (error) {
        setSaveStatus({ state: "failed", category: "weight photo", error: error.message || "Photo upload failed" });
        return false;
      }
    }
    const build = (existing) => ({ ...existing, weight: kg, ...uploaded });
    const updatedLog = weightLog.find(w => w.date === today)
      ? weightLog.map(w => w.date === today ? build(w) : w)
      : [...weightLog, { date: today, weight: kg, ...uploaded }];
    const updatedGoals = { ...fitnessGoals, weightCurrent: kg };
    const weightSaved = await saveCategory("weightLog", updatedLog, weightLog, setWeightLog);
    if (!weightSaved) {
      if (uploaded.photoPath) {
        apiFetch(WEIGHT_IMAGE_URL, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ date: today, path: uploaded.photoPath, previous: previousPhoto }),
        }).catch(() => {});
      }
      return false;
    }
    if (previousPhoto?.storage_path && previousPhoto.storage_path !== uploaded.photoPath) {
      apiFetch(WEIGHT_IMAGE_URL, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date: today, path: uploaded.photoPath, previous: previousPhoto, finalize: true }),
      }).catch(() => {});
    }
    return saveCategory("fitnessGoals", updatedGoals, fitnessGoals, setFitnessGoals);
  }

  // ── System alerts ─────────────────────────────────────────────────────────
  function dismissAlert(id) {
    setSystemAlerts(prev => prev.map(a => a.id === id ? { ...a, read: true } : a));
    apiFetch(ALERTS_URL, {
      method:  "PATCH",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ id }),
    }).catch(() => {});
  }

  function clearAlerts() {
    setSystemAlerts(prev => prev.map(a => ({ ...a, read: true })));
    apiFetch(ALERTS_URL, {
      method:  "PATCH",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({}),
    }).catch(() => {});
  }

  // ── Toast ─────────────────────────────────────────────────────────────────
  function showToast(msg) { setToast(msg); setTimeout(() => setToast(""), 2800); }

  // ── Debug log (persisted) ─────────────────────────────────────────────────
  function addLog(msg) {
    setDebugLog(d => {
      const updated = [...d, msg].slice(-500);
      return updated;
    });
  }

  // ── Meal log helpers ──────────────────────────────────────────────────────
  function getDayTotals(date) {
    const log = logs.find(l => l.date === date);
    if (!log) return null;
    let cal = 0, pro = 0, carb = 0, fat = 0, fib = 0;
    for (const m of Object.values(log.meals || {})) {
      if (m?.macros) { cal += m.macros.calories || 0; pro += m.macros.protein || 0; carb += m.macros.carbs || 0; fat += m.macros.fat || 0; fib += m.macros.fiber || 0; }
    }
    return { calories: cal, protein: pro, carbs: carb, fat, fiber: Math.round(fib * 10) / 10 };
  }

  const sortedLogs = [...logs].sort((a, b) => a.date.localeCompare(b.date));

  return {
    // cloud state
    dataLoading, dataLoadError, saveStatus, retryFailedWrites,
    // navigation
    view, setView,
    // meal plans
    mealPlans, saveMealPlans,
    weekAssignments, saveWeekAssignments,
    getActivePlan,
    // meal log
    logs, saveLogs, sortedLogs, getDayTotals,
    activeMeal, setActiveMeal,
    logDate, setLogDate,
    liveMacros,
    // grocery
    groceries, saveGroceries,
    projections, setProj,
    suggestions, setSuggestions,
    loadingSug, setLoadingSug,
    groceryInput, setGroceryInput,
    groceryAmt, setGroceryAmt,
    groceryUnit, setGroceryUnit,
    // goals
    macroGoals, saveMacroGoals,
    fitnessGoals, saveFitnessGoals,
    editFitnessGoals, setEditFitnessGoals,
    editMacroGoals, setEditMacroGoals,
    visibleGoalCards, setVisibleGoalCards,
    // custom food library
    customFoods, saveCustomFoods,
    // workouts & weight
    workouts, saveWorkouts, deleteFitnessWorkout,
    weightLog,
    healthConnect,
    todayWeighIn, setTodayWeighIn,
    saveWeightEntry,
    progressTab, setProgressTab,
    parsingWorkout, setParsing2,
    // ui
    toast, showToast,
    parsing, setParsing,
    uploadProgress, setUploadProgress,
    parseErr, setParseErr,
    chatMessages, chatApiMessages, saveChatHistory,
    apiUsage, saveApiUsage,
    debugLog, setDebugLog, addLog,
    showDebug, setShowDebug,
    chartMetric, setChartMetric,
    timeRange, setTimeRange,
    systemAlerts, dismissAlert, clearAlerts,
    authRequired, unlockApi, logoutApi,
    refreshRemoteState,
    // refs
    mealFileRef, groceryFileRef, watchFileRef,
    // migration
    forceMigrate,
  };
}
